import {
  addEdge,
  Background,
  BackgroundVariant,
  Connection,
  ConnectionMode,
  Controls,
  Edge,
  EdgeChange,
  MiniMap,
  Node,
  NodeChange,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { TWorkflowStepDefinition, WorkflowStepType } from '@commenda/types';
import { useHotkeys } from 'react-hotkeys-hook';
import { v4 as uuidv4 } from 'uuid';
import { DSLToFlowAdapter, FlowToDSLAdapter } from '../adapters';
import { useWorkflow } from '../context/WorkflowContext';
import { ServiceAuditorEditorHelper } from '../helpers';
import { nodeTypes } from '../nodes';
import {
  TBaseNodeData,
  TRIGGER_NODE_TYPE,
  TTriggerNodeData,
} from '../types/node';

interface WorkflowCanvasProps {
  onNodeSelect?: (nodeId: string | null) => void;
  onNodeDelete?: () => void;
}

const WorkflowCanvasInner = ({
  onNodeSelect,
  onNodeDelete,
}: WorkflowCanvasProps) => {
  const {
    workflow,
    setWorkflow,
    addStep,
    removeStep,
    updateWorkflow,
    addTransition,
    removeTransition,
    setSelectedEntities,
    undo,
    redo,
    addTrigger,
    removeTrigger,
    isReadOnly,
  } = useWorkflow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodePositions, setNodePositions] = useState<
    Map<string, { x: number; y: number }>
  >(new Map());
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow<Node, Edge>();

  // Sync DSL to React Flow
  useEffect(() => {
    if (!workflow) return;

    const { nodes: flowNodes, edges: flowEdges } =
      DSLToFlowAdapter.dslToFlow(workflow);
    setNodes(flowNodes);
    setEdges(flowEdges);
    setNodePositions(FlowToDSLAdapter.extractNodePositions(flowNodes));
  }, [workflow, setNodes, setEdges]);

  // Handle node selection
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (
        node.data.type === WorkflowStepType.START ||
        node.data.type === WorkflowStepType.END
      )
        return;

      if (node.type === TRIGGER_NODE_TYPE) {
        setSelectedEntities([{ type: 'trigger', id: node.id }]);
        if (onNodeSelect) onNodeSelect(node.id);
        return;
      }

      setSelectedEntities([{ type: 'node', id: node.id }]);
      if (onNodeSelect) {
        onNodeSelect(node.id);
      }
    },
    [setSelectedEntities, onNodeSelect]
  );

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      setSelectedEntities([{ type: 'edge', id: edge.id }]);
    },
    [setSelectedEntities]
  );

  // Handle clicking on empty canvas
  const onPaneClick = useCallback(() => {
    setSelectedEntities([]);
    if (onNodeSelect) {
      onNodeSelect(null);
    }
  }, [setSelectedEntities, onNodeSelect]);

  // Handle node position changes
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      changes.forEach((change) => {
        if (change.type === 'position' && change.position && !change.dragging) {
          const isTrigger = workflow?.triggers?.some((t) => t.id === change.id);
          if (isTrigger) {
            setWorkflow({
              ...workflow,
              triggers: workflow?.triggers?.map((t) =>
                t.id === change.id
                  ? { ...t, ui: { reactFlow: { position: change.position } } }
                  : t
              ),
            });
          } else {
            const stepDefinition = workflow?.steps.find(
              (step) => step.id === change.id
            );
            if (stepDefinition) {
              setWorkflow({
                ...workflow,
                steps: workflow?.steps.map((step) =>
                  step.id === change.id
                    ? {
                        ...step,
                        ui: { reactFlow: { position: change.position } },
                      }
                    : step
                ),
              });
            }
          }
          setNodePositions((prev) => {
            const newMap = new Map(prev);
            newMap.set(change.id, change.position);
            return newMap;
          });
        }
      });
    },
    [onNodesChange, workflow, setWorkflow]
  );

  // Handle edge changes
  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes);

      changes.forEach((change) => {
        if (change.type === 'remove') {
          removeTransition(change.id);
        }
      });
    },
    [onEdgesChange, removeTransition]
  );

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      if (!params.source || !params.target) return;
      const edgeId = uuidv4();

      const newTransition = {
        id: edgeId,
        fromStepId: params.source,
        toStepId: params.target,
        label: '',
        ui: {
          reactFlow: {
            handle: {
              sourceHandle: params.sourceHandle,
              targetHandle: params.targetHandle,
            },
          },
        },
      };

      addTransition(newTransition);

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            id: edgeId,
            type: 'step',
          },
          eds
        )
      );
    },
    [addTransition, setEdges]
  );

  // Handle node deletion
  const onNodesDelete = useCallback(
    (deletedNodes: Node[]) => {
      if (onNodeDelete) {
        onNodeDelete();
      }
      deletedNodes.forEach((node) => {
        if (node.type === TRIGGER_NODE_TYPE) {
          removeTrigger(node.id);
        } else {
          removeStep(node.id);
        }
      });
    },
    [removeStep, removeTrigger, onNodeDelete]
  );

  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      deletedEdges.forEach((edge) => {
        removeTransition(edge.id);
      });
    },
    [removeTransition]
  );

  // Handle drag and drop
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const { dataTransfer } = event;
    if (dataTransfer) {
      dataTransfer.dropEffect = 'move';
    }
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      // The ReactFlow interactivity flags below cover dragging, connecting and deleting, but a drop from the
      // palette creates a step through our own handler, so it needs its own guard.
      if (isReadOnly) return;

      if (!reactFlowWrapper.current) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const data = event.dataTransfer.getData('application/reactflow');

      if (!data) return;

      try {
        const { nodeType, nodeData } = JSON.parse(data) as {
          nodeType: string;
          nodeData: TBaseNodeData | TTriggerNodeData;
        };

        const position = screenToFlowPosition({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        if (nodeType === TRIGGER_NODE_TYPE) {
          const triggerData = nodeData as TTriggerNodeData;
          addTrigger({
            id: triggerData.id,
            type: triggerData.triggerType,
            eventType: triggerData.eventType,
            name: triggerData.name,
            ui: { reactFlow: { position } },
          });

          setNodePositions((prev) => {
            const newMap = new Map(prev);
            newMap.set(triggerData.id, position);
            return newMap;
          });

          const newNode: Node = {
            id: triggerData.id,
            type: TRIGGER_NODE_TYPE,
            position,
            data: triggerData,
          };
          setNodes((nds) => nds.concat(newNode));
          return;
        }

        // One workflow update, so the preset steps arrive and undo together; the DSL sync effect draws them.
        if (
          nodeType ===
          ServiceAuditorEditorHelper.DRAFT_AUDITOR_TEMPLATE_NODE_TYPE
        ) {
          updateWorkflow((current) =>
            ServiceAuditorEditorHelper.insertDraftAuditor(current, {
              agentStepId: nodeData.id,
              position,
            })
          );
          return;
        }

        const stepData = nodeData as TBaseNodeData;
        const newStep: TWorkflowStepDefinition = {
          ...stepData,
          ui: {
            reactFlow: {
              position,
            },
          },
        };

        addStep(newStep);

        setNodePositions((prev) => {
          const newMap = new Map(prev);
          newMap.set(newStep.id, position);
          return newMap;
        });

        const newNode: Node = {
          id: newStep.id,
          type: nodeType,
          position,
          data: stepData,
        };

        setNodes((nds) => nds.concat(newNode));
      } catch (error) {
        console.error('Drop error:', error);
      }
    },
    [
      addStep,
      addTrigger,
      updateWorkflow,
      setNodes,
      screenToFlowPosition,
      isReadOnly,
    ]
  );

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (!sourceNode || !targetNode) return false;

      if (sourceNode.type === TRIGGER_NODE_TYPE) {
        return targetNode.data?.type === WorkflowStepType.START;
      }

      if (targetNode.type === TRIGGER_NODE_TYPE) {
        return false;
      }

      return true;
    },
    [nodes]
  );

  useHotkeys('ctrl+z', (e) => {
    e.preventDefault();
    undo();
  });

  useHotkeys('ctrl+y', (e) => {
    e.preventDefault();
    redo();
  });

  useHotkeys('delete', (e) => {
    e.preventDefault();
    console.log('Delete');
  });

  return (
    <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
        isValidConnection={isValidConnection}
        connectionMode={ConnectionMode.Strict}
        // Selection stays enabled while read-only so a node can still be inspected; only writes are
        // blocked, and deleteKeyCode null is what disables delete for nodes and edges alike.
        nodesDraggable={!isReadOnly}
        nodesConnectable={!isReadOnly}
        edgesReconnectable={!isReadOnly}
        deleteKeyCode={isReadOnly ? null : 'Backspace'}
      >
        <Controls />
        <MiniMap
          style={{
            height: 120,
            width: 160,
          }}
          zoomable
          pannable
        />
        <Background
          variant={BackgroundVariant.Dots}
          gap={32}
          size={2}
          color="#b1ada7"
          className="bg-bg-secondary"
        />
      </ReactFlow>
    </div>
  );
};

export const WorkflowCanvas = (props: WorkflowCanvasProps) => {
  return <WorkflowCanvasInner {...props} />;
};
